import { BookOpen, Pencil, Trash2 } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { Button } from "../client/components/ui/button";
import { Card } from "../client/components/ui/card";
import { Badge } from "../client/components/ui/badge";
import type { PlaybookSetup } from "./constants";

type PlaybookCardProps = {
  playbook: any;
  onEdit: () => void;
  onDelete: () => void;
};

export default function PlaybookCard({ playbook, onEdit, onDelete }: PlaybookCardProps) {
  const t = useTranslation();
  const { language } = useLanguageContext();

  const setups = (playbook.setups as PlaybookSetup[]) ?? [];
  const createdAt = new Date(playbook.createdAt).toLocaleDateString(
    language === "fr" ? "fr-FR" : "en-US"
  );
  const targetAccounts = playbook.targetAccounts ?? [];
  const targetGroups = playbook.targetGroups ?? [];

  return (
    <Card className="p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <BookOpen className="h-4 w-4 text-primary shrink-0" />
          <span className="font-medium truncate">{playbook.name ?? playbook.strategyName}</span>
          <Badge variant="outline">
            {t.playbook.versionLabel.replace("{version}", String(playbook.version))}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {t.playbook.createdOn.replace("{date}", createdAt)}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label={t.playbook.edit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} aria-label={t.playbook.deleteAction}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {playbook.markets.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {playbook.markets.map((m: string) => (
            <Badge key={m} variant="secondary" className="font-mono">
              {m}
            </Badge>
          ))}
        </div>
      )}

      {(targetAccounts.length > 0 || targetGroups.length > 0) && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t.playbook.targetsLabel}</span>
          <div className="flex flex-wrap gap-1.5">
            {targetAccounts.map((a: any) => (
              <Badge key={a.id} variant="outline">
                {a.name}
              </Badge>
            ))}
            {targetGroups.map((g: any) => (
              <Badge key={g.id} variant="outline">
                {g.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {setups.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t.playbook.setupsStep.setupsLabel}</span>
          {setups.map((s, i) => (
            <div key={i} className="rounded-md border border-border p-2">
              <p className="text-sm font-medium">{s.name}</p>
              {s.entryRules && <p className="text-xs text-muted-foreground mt-1">{s.entryRules}</p>}
            </div>
          ))}
        </div>
      )}

      {playbook.personalRules && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t.playbook.rulesStep.personalRulesLabel}</span>
          <p className="text-sm text-muted-foreground whitespace-pre-line">{playbook.personalRules}</p>
        </div>
      )}
    </Card>
  );
}
